// app/src/lib/scale/audit.js — append-only audit log (US-5.5).
//
// Hard invariants:
//   - Entries are append-only: this module exposes `record()` and read queries; no
//     update / delete is ever exposed.
//   - Every mutation in the rest of the lib should call `audit.record(...)` before
//     or after the change. (Phase 5 starts wiring this; older phases get
//     instrumented in a follow-up.)
//   - Records mirror to S3 (append-only) via the `audit-mirror` Lambda. The
//     mirror is best-effort; if it fails the local row is still authoritative.
//
// Privacy:
//   - `diff` should NEVER contain sensitive fields (TIN, raw transaction amounts
//     beyond what's in the entity itself, etc.). Callers are responsible.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';

/** @typedef {'create'|'update'|'delete'|'approve'|'reject'|'export'|'invite'|'revoke'} AuditAction */
/** @typedef {{ auditId:string, ownerId:string, actorId:string, ts:number, action:AuditAction, entityType:string, entityId:string, diff?:object, meta?:object }} AuditEntry */

/**
 * Append a new audit row. `before` and `after` are diffed into `diff` if present.
 * Pass `ownerId` explicitly when the actor is not the owner (e.g. invited member).
 *
 * @param {{ ownerId?:string, action:AuditAction, entityType:string, entityId:string, before?:object, after?:object, meta?:object }} entry
 */
export async function record(entry) {
  // Never block a mutation because of a logging failure — fail soft.
  try {
    const claims = await currentClaims();
    if (!claims) return null;
    const ownerId = entry.ownerId || claims.sub;
    const row = {
      auditId: crypto.randomUUID(),
      ownerId,
      actorId: claims.sub,
      ts: Date.now(),
      action: entry.action,
      entityType: entry.entityType,
      entityId: entry.entityId,
      diff: buildDiff(entry.before, entry.after),
      meta: entry.meta,
    };
    await db.auditEntries.add(row);
    return row;
  } catch (e) {
    console.warn('[audit] record failed', e);
    return null;
  }
}

/** Owner-only by convention; the caller (UI) checks capability with rbac.can. */
export async function list({ from, to, actorId } = {}) {
  const claims = await currentClaims();
  if (!claims) return [];
  const ownerId = claims.sub;
  let rows = await db.auditEntries
    .where('[ownerId+ts]')
    .between([ownerId, from ? new Date(from).getTime() : 0], [ownerId, to ? new Date(to).getTime() : Number.MAX_SAFE_INTEGER])
    .toArray();
  if (actorId) rows = rows.filter((r) => r.actorId === actorId);
  return rows;
}

/** CSV export (read-only operation; UI gates on `audit.export`). */
export async function exportCsv(opts) {
  const rows = await list(opts);
  const lines = ['ts,actor,action,entityType,entityId,summary'];
  for (const r of rows) {
    const summary = r.diff ? Object.keys(r.diff).join('|') : '';
    lines.push([
      new Date(r.ts).toISOString(),
      csvField(r.actorId),
      csvField(r.action),
      csvField(r.entityType),
      csvField(r.entityId),
      csvField(summary),
    ].join(','));
  }
  return lines.join('\n');
}

const SENSITIVE = new Set([
  'tin', 'tinCiphertextB64u',
  'accessToken', 'refreshToken', 'idToken',
  'plaidAccessToken', 'webhookSecret', 'apiKey',
]);

function buildDiff(before, after) {
  if (!before && !after) return undefined;
  if (!before) return { __created__: true, after: shallowSafe(after) };
  if (!after)  return { __deleted__: true, before: shallowSafe(before) };
  const diff = {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const k of keys) {
    if (before[k] === after[k]) continue;
    if (JSON.stringify(before[k]) === JSON.stringify(after[k])) continue;
    // Per-key redaction: never let a sensitive field land in an update diff.
    if (SENSITIVE.has(k)) {
      diff[k] = { before: '[REDACTED]', after: '[REDACTED]' };
    } else {
      diff[k] = { before: before[k], after: after[k] };
    }
  }
  return Object.keys(diff).length ? diff : undefined;
}

/** Drop fields known to carry sensitive data so they never enter the audit log. */
function shallowSafe(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (SENSITIVE.has(k)) { out[k] = '[REDACTED]'; continue; }
    out[k] = v;
  }
  return out;
}

function csvField(s) {
  if (s == null) return '';
  let str = String(s);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}
